import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
	AlertCircle,
	AlertTriangle,
	ArrowRight,
	Bot,
	Check,
	ChevronDown,
	ChevronRight,
	ExternalLink,
	Loader2,
	Plus,
	Undo2,
} from "lucide-react";
import { Button } from "../ui/button";
import { ScrollArea } from "../ui/scroll-area";
import { AIComposer } from "../AIComposer";
import { AIMarkdown } from "../AIMarkdown";
import { AIAgentSetup } from "./AIAgentSetup";
import { SourcePicker } from "./SourcePicker";
import {
	EFFORT_LEVELS,
	describeTool,
	getAgentModels,
	getAgentStatus,
	splitConfirmation,
	streamAgentChat,
	undoAgentSession,
} from "../../lib/aiAgent";

/**
 * Chat agentica nel dock del Program Builder.
 * Spec: docs/ai-program/10-byok-and-mcp-rework.md (Fase 2).
 *
 * Ha sostituito il vecchio drawer AI. Le differenze che si vedono usandolo:
 *
 * - **il testo arriva mentre viene scritto**, non dopo 30-60 secondi di
 *   spinner (problema #4 di docs/ai-program/02-current-state-analysis.md);
 * - **si vede cosa sta facendo**: ogni strumento usato compare in chiaro, cosi'
 *   l'attesa ha una spiegazione invece di essere muta;
 * - **non restituisce blocchi da copiare a mano**: scrive il programma
 *   direttamente, e qui compare il link per aprirlo.
 */
const formatElapsed = (seconds) =>
	seconds < 60
		? `${seconds}s`
		: `${Math.floor(seconds / 60)}m ${seconds % 60}s`;

const formatTokens = (value) =>
	value >= 1000 ? `${(value / 1000).toFixed(1)}k` : String(value || 0);

/**
 * Che cosa dire al coach mentre aspetta, evento per evento.
 *
 * `opened` arriva prima che il modello faccia qualsiasi cosa e serve a una cosa
 * sola: se l'etichetta non si muove entro il primo secondo, lo stream non sta
 * arrivando — qualcosa a monte lo sta trattenendo — e non e' il modello che e'
 * lento (vedi `_SSE_HEADERS` in routes/ai_agent.py).
 */
const describeStatus = (data) => {
	if (data.step === "opened") return "Collegato, preparo il contesto";
	if (data.step === "working") return "Il modello sta ragionando";
	if (data.step === "budget" || data.step === "max_steps") return data.label;
	return "Preparo il passaggio";
};

/**
 * Cronologia di cio' che l'agente ha fatto, riga per riga.
 *
 * Sostituisce una scritta di stato che cambiava: con quella, quattro minuti di
 * attesa erano indistinguibili da un blocco, e a fine corsa non restava traccia
 * di cosa fosse stato controllato. Qui ogni passaggio e ogni strumento restano,
 * con il loro esito e il tempo impiegato.
 */
function AgentTimeline({ steps, running }) {
	const [open, setOpen] = useState(true);

	return (
		<div className="rounded-sm border border-border bg-secondary/30">
			<button
				type="button"
				onClick={() => setOpen((value) => !value)}
				className="flex w-full items-center gap-1.5 px-2 py-1.5 text-[10px] uppercase tracking-wide text-muted-foreground hover:text-foreground"
			>
				{open ? (
					<ChevronDown className="h-3 w-3" />
				) : (
					<ChevronRight className="h-3 w-3" />
				)}
				{steps.length} {steps.length === 1 ? "operazione" : "operazioni"}
				{running && <span className="text-volt">· in corso</span>}
			</button>

			{open && (
				<ol className="space-y-0.5 border-t border-border/60 px-2 py-1.5">
					{steps.map((entry, index) => (
						<li
							key={`${entry.at}-${index}`}
							className="flex items-start gap-1.5 text-[11px] leading-relaxed"
						>
							<span className="mt-[3px] shrink-0">
								{entry.kind === "step" ? (
									<ChevronRight className="h-3 w-3 text-muted-foreground" />
								) : entry.pending ? (
									<Loader2 className="h-3 w-3 animate-spin text-volt" />
								) : entry.ok === false ? (
									<AlertCircle className="h-3 w-3 text-blaze" />
								) : (
									<Check className="h-3 w-3 text-volt" />
								)}
							</span>
							<span className="min-w-0 flex-1">
								<span
									className={
										entry.kind === "step"
											? "text-muted-foreground"
											: "text-foreground"
									}
								>
									{entry.label}
								</span>
								{entry.detail && (
									<span className="text-muted-foreground">
										{" "}
										· {entry.detail}
									</span>
								)}
								{entry.writes && <span className="text-volt"> · scrive</span>}
								{entry.error && (
									<span className="block text-blaze">{entry.error}</span>
								)}
							</span>
							{entry.durationMs > 0 && (
								<span className="shrink-0 text-muted-foreground/60">
									{entry.durationMs < 1000
										? `${entry.durationMs}ms`
										: `${(entry.durationMs / 1000).toFixed(1)}s`}
								</span>
							)}
						</li>
					))}
				</ol>
			)}
		</div>
	);
}

/**
 * Cosa ha scritto l'assistente, e come riportarlo indietro.
 *
 * Dopo la terza prova il coach si e' trovato due settimane gia' salvate senza
 * averle confermate. Chiedere conferma a ogni scrittura non e' la risposta —
 * una sessione sono decine di chiamate, e ventisei finestre si imparano a
 * cliccare senza leggere. La risposta e' sapere **cosa** e' cambiato e poterlo
 * disfare in un gesto.
 */
function ChangeSummary({ scope, onUndone }) {
	const [undoing, setUndoing] = useState(false);

	if (scope.undone) {
		return (
			<p className="text-[11px] text-muted-foreground">
				Modifiche annullate: i programmi sono tornati come prima.
			</p>
		);
	}

	const undo = async () => {
		if (
			!window.confirm(
				"I programmi tornano com'erano prima di questa conversazione. " +
					"Le modifiche che hai fatto a mano nel frattempo andranno perse. Procedo?",
			)
		) {
			return;
		}
		setUndoing(true);
		try {
			await undoAgentSession(scope.session_id);
			toast.success("Modifiche annullate");
			onUndone?.();
		} catch (error) {
			toast.error(error.message || "Impossibile annullare");
		} finally {
			setUndoing(false);
		}
	};

	return (
		<div className="rounded-sm border border-volt/40 bg-volt/5 p-2">
			<p className="mb-1 text-[10px] uppercase tracking-wide text-muted-foreground">
				Modifiche salvate
			</p>
			<ul className="mb-2 space-y-0.5">
				{scope.changes.map((change, index) => (
					<li
						key={index}
						className={`text-[11px] ${change.destructive ? "text-blaze" : ""}`}
					>
						· {change.summary}
					</li>
				))}
			</ul>
			{scope.destructive_changes > 0 && (
				<p className="mb-2 text-[10px] text-blaze">
					{scope.destructive_changes === 1
						? "Una di queste modifiche ha cancellato qualcosa."
						: `${scope.destructive_changes} di queste modifiche hanno cancellato qualcosa.`}
				</p>
			)}
			{scope.can_undo && (
				<Button
					type="button"
					size="sm"
					variant="ghost"
					onClick={undo}
					disabled={undoing}
					className="h-7 rounded-sm text-[11px] text-muted-foreground hover:text-blaze"
				>
					<Undo2 className="mr-1.5 h-3 w-3" />
					{undoing ? "Annullo…" : "Annulla tutto"}
				</Button>
			)}
		</div>
	);
}

/**
 * Il cancello, visto dal coach.
 *
 * Qui si rompe deliberatamente la regola di §9quinquies — «non chiediamo
 * conferma a ogni scrittura, ventisei finestre non sono controllo». Quella
 * regola vale per le scritture che *aggiungono*: un esercizio di troppo si
 * vede e si toglie. Non vale per quelle che cancellano, dove l'informazione
 * che sparisce non e' recuperabile guardando lo schermo, e dove quindi la
 * conferma e' l'unico momento in cui il coach puo' ancora decidere.
 *
 * Non e' una `window.confirm`: il blocco resta nella conversazione, dice
 * **cosa** sparirebbe (lo ha scritto l'assistente sopra) e si ignora
 * semplicemente scrivendo altro. Una finestra modale che si piazza davanti
 * allo schermo e' esattamente il tipo di conferma che si impara a chiudere.
 */
function DestructiveConfirm({ pending, intent, disabled, onConfirm }) {
	return (
		<div className="rounded-sm border border-blaze/50 bg-blaze/10 p-2.5">
			<div className="mb-1.5 flex items-start gap-2">
				<AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-blaze" />
				<div className="min-w-0">
					<p className="text-[11px] font-medium text-blaze">
						Serve il tuo via libera: questa operazione cancella
					</p>
					{pending.detail && (
						<p className="mt-0.5 text-[11px] text-muted-foreground">
							L'assistente vuole {pending.detail}.
						</p>
					)}
				</div>
			</div>
			<p className="mb-2 text-[10px] leading-relaxed text-muted-foreground">
				Quello che viene tolto non torna da solo: resta «Annulla tutto», che
				però riporta indietro <strong>tutta</strong> la conversazione. Il via
				libera vale solo per questo passaggio.
			</p>
			<Button
				type="button"
				size="sm"
				disabled={disabled}
				onClick={() => onConfirm(intent)}
				className="h-7 rounded-sm border border-blaze/50 bg-blaze/15 text-[11px] text-blaze hover:bg-blaze/25"
			>
				<AlertTriangle className="mr-1.5 h-3 w-3" />
				Confermo, procedi
			</Button>
		</div>
	);
}

/** Scelta dell'intensita' di ragionamento, con la spiegazione accanto. */
function EffortPicker({ value, onChange }) {
	const active = EFFORT_LEVELS.find((level) => level.value === value);
	return (
		<div className="mb-2 flex flex-wrap items-center gap-1">
			{EFFORT_LEVELS.map((level) => (
				<button
					key={level.value}
					type="button"
					onClick={() => onChange(level.value)}
					title={level.hint}
					className={`rounded-sm border px-2 py-0.5 text-[10px] uppercase tracking-wide transition-colors ${
						level.value === value
							? "border-volt/50 bg-volt/10 text-volt"
							: "border-border text-muted-foreground hover:text-foreground"
					}`}
				>
					{level.label}
				</button>
			))}
			{active && (
				<span className="w-full text-[10px] text-muted-foreground/80">
					{active.hint}
				</span>
			)}
		</div>
	);
}

export function AgentChatPanel({
	programId,
	athleteIds = [],
	onProgramCreated,
	// Il modello prestativo aperto, o lo sport della pagina «Sport e modelli»:
	// solo con uno dei due l'assistente scrive sui modelli, e solo li'
	// (services/ai_agent/guard.py, §9octodecies della spec).
	modelId = null,
	modelSport = null,
	onModelsChanged,
}) {
	const [status, setStatus] = useState(null);
	const [models, setModels] = useState([]);
	const [model, setModel] = useState(null);
	const [effort, setEffort] = useState("fast");
	// Fonti fissate per la conversazione: documenti di piattaforma, indirizzi,
	// ed eventualmente la ricerca online dove il provider la espone.
	const [documentIds, setDocumentIds] = useState([]);
	const [urls, setUrls] = useState([]);
	const [webSearch, setWebSearch] = useState(false);
	const [messages, setMessages] = useState([]);
	// La conversazione vive sul server, risultati dei tool compresi: qui si
	// tiene solo la chiave per continuarla. E' cio' che rende un «procedi» una
	// ripresa e non una seconda richiesta da zero.
	const [sessionId, setSessionId] = useState(null);
	const [input, setInput] = useState("");
	const [isLoading, setIsLoading] = useState(false);
	const [activity, setActivity] = useState(null);
	// L'attesa dura minuti: senza un contatore che si muove e' indistinguibile
	// da un blocco. E' il primo riscontro arrivato dalla prova sul campo.
	const [elapsed, setElapsed] = useState(0);
	const abortRef = useRef(null);
	const scrollRef = useRef(null);

	const loadStatus = useCallback(async () => {
		try {
			const agentStatus = await getAgentStatus();
			setStatus(agentStatus);
			if (!agentStatus.configured) return;
			const { models: list } = await getAgentModels();
			setModels(list || []);
			// Default = il primo che propone il provider, non il piu' economico
			// scelto da noi (ADR-107).
			setModel((current) => current || list?.[0]?.id || null);
		} catch (error) {
			setStatus((prev) => prev || { configured: false, byok_available: true });
			setActivity({ error: error.message });
		}
	}, []);

	useEffect(() => {
		loadStatus();
	}, [loadStatus]);

	useEffect(() => {
		const node = scrollRef.current;
		if (node) node.scrollTop = node.scrollHeight;
	}, [messages, activity]);

	useEffect(() => {
		if (!isLoading) return undefined;
		setElapsed(0);
		const started = Date.now();
		const timer = setInterval(
			() => setElapsed(Math.round((Date.now() - started) / 1000)),
			1000,
		);
		return () => clearInterval(timer);
	}, [isLoading]);

	const patchMessage = useCallback((index, patch) => {
		setMessages((prev) => {
			const next = [...prev];
			next[index] = { ...next[index], ...patch };
			return next;
		});
	}, []);

	const handleStop = useCallback(() => {
		abortRef.current?.abort();
		setIsLoading(false);
		setActivity(null);
	}, []);

	/**
	 * Ricomincia da capo, scordando la conversazione sul server.
	 *
	 * Serve perche' riprendere ha un costo: ogni messaggio si porta dietro tutto
	 * quello che l'assistente ha letto finora. Quando l'argomento cambia, quel
	 * contesto non aiuta piu' — si paga e confonde.
	 */
	const handleNewConversation = useCallback(() => {
		abortRef.current?.abort();
		abortRef.current = null;
		setMessages([]);
		setSessionId(null);
		setActivity(null);
		setIsLoading(false);
	}, []);

	// `override` arriva dai pulsanti di conferma. Il controllo sul tipo non e'
	// pignoleria: il bottone del composer passa a `onSubmit` l'evento del clic.
	//
	// `confirmDestructive` e' il via libera a cancellare, e vale **solo per
	// questo messaggio**: il server non lo conserva nella sessione, quindi il
	// turno dopo riparte chiuso. Lo passa un pulsante solo — quello rosso che
	// compare dopo che una cancellazione si e' fermata al cancello — e mai il
	// composer, o basterebbe scrivere «vai» per autorizzarla.
	const handleSubmit = async (
		override,
		{ confirmDestructive = false } = {},
	) => {
		const text = (typeof override === "string" ? override : input).trim();
		if (!text || isLoading || !model) return;

		const history = messages.map(({ role, content }) => ({ role, content }));
		setMessages((prev) => [
			...prev,
			{ role: "user", content: text },
			{ role: "assistant", content: "", streaming: true, steps: [] },
		]);
		setInput("");
		setIsLoading(true);
		setActivity({ label: "Leggo la richiesta" });

		const controller = new AbortController();
		abortRef.current = controller;

		const patchAssistant = (patch) =>
			setMessages((prev) => {
				const next = [...prev];
				const last = next.length - 1;
				next[last] = { ...next[last], ...patch(next[last]) };
				return next;
			});

		const appendStep = (entry) =>
			patchAssistant((msg) => ({
				steps: [...(msg.steps || []), { ...entry, at: Date.now() }],
			}));

		try {
			await streamAgentChat(
				{
					message: text,
					model,
					session_id: sessionId,
					// Resta come rete: se la sessione e' scaduta sul server, il
					// modello riparte almeno dal filo del discorso invece che dal
					// nulla.
					conversation_history: history,
					program_id: programId || null,
					model_id: modelId || null,
					model_sport: modelSport || null,
					athlete_ids: athleteIds,
					effort,
					document_ids: documentIds,
					reference_urls: urls,
					web_search: webSearch,
					confirm_destructive: confirmDestructive,
				},
				{
					signal: controller.signal,
					onStatus: (data) => {
						setActivity({
							label: describeStatus(data),
							step: data.iteration,
							maxSteps: data.max_steps,
							tokens: data.billed_tokens,
							warn: data.step === "budget",
						});
						// Un passaggio nuovo apre una riga nella cronologia; il
						// battito aggiorna solo quella in corso.
						if (data.step === "thinking") {
							appendStep({
								kind: "step",
								label: `Passaggio ${data.iteration}`,
							});
						}
					},
					onDelta: (data) =>
						patchAssistant((msg) => ({
							content: msg.content + (data.text || ""),
						})),
					onToolCall: (data) => {
						setActivity((prev) => ({
							...(prev || {}),
							label: describeTool(data.name),
							detail: data.detail,
							writes: data.writes,
							warn: false,
						}));
						appendStep({
							kind: "tool",
							label: describeTool(data.name),
							detail: data.detail,
							writes: data.writes,
							pending: true,
						});
					},
					onToolResult: (data) => {
						// Si chiude la riga aperta dalla chiamata corrispondente.
						patchAssistant((msg) => {
							const steps = [...(msg.steps || [])];
							for (let i = steps.length - 1; i >= 0; i -= 1) {
								if (steps[i].kind === "tool" && steps[i].pending) {
									steps[i] = {
										...steps[i],
										pending: false,
										ok: data.ok,
										detail: data.detail || steps[i].detail,
										error: data.message,
										durationMs: data.duration_ms,
									};
									break;
								}
							}
							return { steps };
						});
						if (!data.ok) {
							setActivity((prev) => ({
								...(prev || {}),
								label: "Correggo e riprovo",
								detail: null,
								warn: true,
							}));
						}
					},
					onConfirmRequired: (data) =>
						patchAssistant(() => ({ needsConfirmation: data })),
					onDone: (data) => {
						if (data.session_id) setSessionId(data.session_id);
						patchAssistant((msg) => ({
							streaming: false,
							content: data.response || "",
							usage: data.usage,
							totalSteps: data.steps,
							scope: data.scope,
							sources: data.sources,
							stoppedOnBudget: data.stopped_on_budget,
							programId: data.program_id,
							needsConfirmation:
								data.needs_confirmation || msg.needsConfirmation || null,
						}));
						if (data.program_id) onProgramCreated?.(data.program_id);
						const touched = [
							...(data.scope?.changed_models || []),
							...(data.scope?.created_models || []),
						];
						if (touched.length > 0) onModelsChanged?.(touched);
					},
					onError: (data) =>
						patchAssistant(() => ({
							streaming: false,
							error: data.message || "Errore dell'assistente",
						})),
				},
			);
		} catch (error) {
			if (error.name !== "AbortError") {
				patchAssistant(() => ({ streaming: false, error: error.message }));
			}
		} finally {
			setIsLoading(false);
			setActivity(null);
			abortRef.current = null;
		}
	};

	if (!status) {
		return (
			<div className="flex h-full items-center justify-center text-muted-foreground">
				<Loader2 className="h-5 w-5 animate-spin" />
			</div>
		);
	}

	if (!status.configured) {
		// Il setup sta qui dentro, non dietro un link: chi vuole usare
		// l'assistente lo vuole usare adesso, non dopo un giro in Impostazioni.
		return (
			<ScrollArea className="h-full">
				<div className="space-y-4 p-4">
					<div className="flex items-start gap-3">
						<Bot className="mt-0.5 h-6 w-6 shrink-0 text-volt" />
						<div>
							<p className="font-heading text-sm">Attiva l'assistente</p>
							<p className="text-xs text-muted-foreground">
								Scrive i programmi dentro l'app: esercizi, serie e carichi,
								senza ricopiare niente a mano.
							</p>
						</div>
					</div>
					<AIAgentSetup status={status} onChanged={loadStatus} />
				</div>
			</ScrollArea>
		);
	}

	return (
		<div className="flex h-full flex-col">
			<ScrollArea className="flex-1">
				<div ref={scrollRef} className="space-y-4 p-3">
					{messages.length === 0 && (
						<div className="rounded-sm border border-border bg-secondary/40 p-3 text-xs text-muted-foreground">
							<p className="mb-2 font-medium text-foreground">
								Scrivi cosa ti serve, in parole tue.
							</p>
							<p>
								«4 settimane di forza per Marco, 3 sedute, focus panca» — creo
								la bozza con esercizi e serie già dentro, non pubblicata.
							</p>
						</div>
					)}

					{messages.map((message, index) => {
						if (message.role === "user") {
							return (
								<div key={index} className="flex justify-end">
									<div className="max-w-[85%] rounded-sm bg-volt/10 px-3 py-2 text-sm">
										{message.content}
									</div>
								</div>
							);
						}

						// La riga `[CONFERMA]` esce dal testo e diventa un pulsante: e'
						// il punto in cui prima il coach restava con un piano pronto
						// davanti e nessun modo evidente di dire «vai».
						const { body, confirm } = splitConfirmation(message.content);
						const isLast = index === messages.length - 1;
						// Una cancellazione fermata al cancello ha la precedenza sul
						// «Procedi» normale: sono due pulsanti che mandano messaggi
						// diversi, e mostrarli insieme vorrebbe dire offrire al coach
						// una scorciatoia che salta l'avviso.
						const pendingDestructive =
							message.needsConfirmation && !message.streaming && isLast
								? message.needsConfirmation
								: null;
						const awaitingGo =
							!pendingDestructive &&
							confirm !== null &&
							!message.streaming &&
							!message.error &&
							isLast;

						return (
							<div key={index} className="space-y-2">
								{message.steps?.length > 0 && (
									<AgentTimeline
										steps={message.steps}
										running={message.streaming}
									/>
								)}
								{message.error ? (
									<div className="rounded-sm border border-blaze/40 bg-blaze/10 p-2 text-sm text-blaze">
										{message.error}
									</div>
								) : (
									<AIMarkdown content={body} />
								)}
								{pendingDestructive && (
									<DestructiveConfirm
										pending={pendingDestructive}
										intent={
											confirm
												? `Procedi: ${confirm}`
												: `Confermo: ${pendingDestructive.detail || "procedi"}`
										}
										disabled={isLoading}
										onConfirm={(intent) =>
											handleSubmit(intent, { confirmDestructive: true })
										}
									/>
								)}
								{awaitingGo && (
									<div className="flex flex-wrap items-center gap-x-2 gap-y-1">
										<Button
											type="button"
											size="sm"
											disabled={isLoading}
											onClick={() =>
												handleSubmit(
													confirm ? `Procedi: ${confirm}` : "Procedi",
												)
											}
											className="h-7 rounded-sm border border-volt/50 bg-volt/10 text-[11px] text-volt hover:bg-volt/20"
										>
											<ArrowRight className="mr-1.5 h-3 w-3" />
											Procedi
										</Button>
										{confirm && (
											<span className="min-w-0 flex-1 text-[11px] text-muted-foreground">
												{confirm}
											</span>
										)}
									</div>
								)}
								{message.sources?.length > 0 && (
									<div className="rounded-sm border border-border bg-secondary/30 p-2">
										<p className="mb-1 text-[10px] uppercase tracking-wide text-muted-foreground">
											Fonti consultate
										</p>
										<ul className="space-y-0.5">
											{message.sources.map((source) => (
												<li key={source.url} className="text-[11px]">
													<a
														href={source.url}
														target="_blank"
														rel="noreferrer"
														className="text-volt hover:underline"
													>
														{source.title || source.url}
													</a>
												</li>
											))}
										</ul>
									</div>
								)}
								{message.scope?.changes?.length > 0 && (
									<ChangeSummary
										scope={message.scope}
										onUndone={() => {
											patchMessage(index, {
												scope: { ...message.scope, undone: true },
											});
											onProgramCreated?.(programId);
											if (
												message.scope?.changed_models?.length ||
												message.scope?.created_models?.length
											) {
												onModelsChanged?.([
													...(message.scope.changed_models || []),
													...(message.scope.created_models || []),
												]);
											}
										}}
									/>
								)}
								{/* Il pulsante serve solo se il programma non e' quello
								    gia' aperto: altrimenti e' un invito ad andare dove
								    si e' gia'. */}
								{message.programId && message.programId !== programId && (
									<Button
										asChild
										size="sm"
										variant="outline"
										className="rounded-sm"
									>
										<Link to={`/programs/${message.programId}`}>
											<ExternalLink className="mr-2 h-3.5 w-3.5" />
											Apri il programma
										</Link>
									</Button>
								)}
								{message.usage && (
									<p className="text-[10px] text-muted-foreground">
										{message.totalSteps} passaggi ·{" "}
										{formatTokens(message.usage.input_tokens)} token pagati in
										entrata
										{message.usage.cached_tokens > 0 &&
											` (+${formatTokens(message.usage.cached_tokens)} dalla cache)`}{" "}
										· {formatTokens(message.usage.output_tokens)} in uscita
										{message.stoppedOnBudget && (
											<span className="text-blaze"> · fermato dal limite</span>
										)}
									</p>
								)}
							</div>
						);
					})}

					{activity && (
						<div
							className={`flex flex-wrap items-center gap-x-2 gap-y-1 text-xs ${
								activity.warn ? "text-blaze" : "text-muted-foreground"
							}`}
						>
							<Loader2 className="h-3 w-3 shrink-0 animate-spin" />
							<span>{activity.label}</span>
							{activity.detail && (
								<span className="text-muted-foreground/70">
									· {activity.detail}
								</span>
							)}
							{activity.writes && (
								<span className="text-volt">· scrivo nel programma</span>
							)}
							{activity.step > 0 && (
								<span className="text-muted-foreground/70">
									· passaggio {activity.step}
									{activity.maxSteps ? `/${activity.maxSteps}` : ""}
								</span>
							)}
							<span className="text-muted-foreground/70">
								· {formatElapsed(elapsed)}
							</span>
							{activity.tokens > 0 && (
								<span className="text-muted-foreground/70">
									· {formatTokens(activity.tokens)} token
								</span>
							)}
						</div>
					)}
				</div>
			</ScrollArea>

			<div className="border-t border-border p-2">
				{messages.length > 0 && (
					<div className="mb-2 flex items-center justify-between gap-2">
						<span className="min-w-0 truncate text-[10px] text-muted-foreground">
							{sessionId && "L'assistente ricorda cosa ha già letto qui."}
						</span>
						<Button
							type="button"
							size="sm"
							variant="ghost"
							onClick={handleNewConversation}
							title="Riprendere costa: ogni messaggio si porta dietro tutto quello che l'assistente ha letto finora. Quando cambi argomento, ricomincia."
							className="h-6 shrink-0 rounded-sm px-1.5 text-[10px] text-muted-foreground hover:text-foreground"
						>
							<Plus className="mr-1 h-3 w-3" />
							Nuova
						</Button>
					</div>
				)}
				<SourcePicker
					documentIds={documentIds}
					onDocumentsChange={setDocumentIds}
					urls={urls}
					onUrlsChange={setUrls}
					webSearch={webSearch}
					onWebSearchChange={setWebSearch}
					webSearchAvailable={Boolean(status.supports_web_search)}
				/>
				<EffortPicker value={effort} onChange={setEffort} />
				<AIComposer
					value={input}
					onChange={setInput}
					onSubmit={handleSubmit}
					isLoading={isLoading}
					onStop={handleStop}
					placeholder="Cosa vuoi costruire?"
					model={model}
					availableModels={models}
					onModelChange={setModel}
					testId="agent-chat-input"
				/>
			</div>
		</div>
	);
}
